// 🎁 The buyer's bonus (user 2026-10-21/22) — second tab of the Shopping List page, in Arabic (admin only for now).
// Backend: controllers/buyer-bonus.js. Three rewards, each approved by an admin: 💰 a better buying price (a share of
// the saving), ⏱️ a best seller restocked before it goes under 20 pieces, 🔎 a missing product that works found again.
import { FC, useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

type Kind = 'saving' | 'ontime' | 'found';
interface Reward {
  _id: string; kind: Kind; groupName: string; image: string | null; qty: number; price: number | null; bonus: number; supplier: string | null;
  status: 'pending' | 'approved' | 'refused'; at: string; note?: string | null;
  // saving
  eligibleQty?: number; reference?: number; referenceSource?: 'paid' | 'cost' | 'known'; savingPiece?: number; saving?: number; suspicious?: boolean;
  // commissions
  stockBefore?: number; reportedAt?: string;
}
interface Settings { savingPct: number; monthlyCap: number; stockTop: number; stockMin: number; ontimeAmount: number; foundAmount: number }
interface Month {
  month: string; settings: Settings; final: boolean; total: number; capped: boolean; pending: number;
  parts: { savings: number; ontime: number; found: number };
  items: Reward[];
  bestSellers: { groupId: string; name: string; image: string | null; stock: number }[];
}

const mad = (n: number) => `${Math.round(n).toLocaleString('fr-FR')} درهم`;
const MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'ماي', 'يونيو', 'يوليوز', 'غشت', 'شتنبر', 'أكتوبر', 'نونبر', 'دجنبر'];
const monthName = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const shiftMonth = (m: string, n: number) => { const d = new Date(`${m}-15T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 7); };
const thisMonth = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Casablanca', year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);
const dm = (d: string) => { const x = new Date(d); return `${x.getUTCDate()}/${x.getUTCMonth() + 1}`; };
const card: React.CSSProperties = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,.05)', padding: 12, marginBottom: 10 };
const STATUS = { pending: { t: '⏳ فالانتظار', c: '#b45309', bg: '#fffbeb' }, approved: { t: '✅ مقبولة', c: '#047857', bg: '#ecfdf5' }, refused: { t: '❌ مرفوضة', c: '#b91c1c', bg: '#fef2f2' } } as const;

const Section: FC<{ icon: string; title: string; earned: number; children: React.ReactNode }> = ({ icon, title, earned, children }) => (
  <div style={card}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <div style={{ fontWeight: 900, fontSize: 16 }}>{icon} {title}</div>
      <div style={{ fontWeight: 900, fontSize: 17, color: earned > 0 ? '#047857' : '#94a3b8', whiteSpace: 'nowrap' }}>{mad(earned)}</div>
    </div>
    <div style={{ fontSize: 13.5, color: '#334155', marginTop: 6, lineHeight: 1.6 }}>{children}</div>
  </div>
);

const BuyerBonus: FC<{ api: AxiosInstance; admin: boolean }> = ({ api, admin }) => {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState<Month | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [edit, setEdit] = useState<Settings | null>(null);
  const [showDone, setShowDone] = useState(false);   // admin: look back at decided rewards

  const load = useCallback(async () => {
    setLoading(true);
    try { setD((await api.get('/buyer-bonus', { params: { month } })).data); setErr(null); } catch (e: any) { setErr(e?.response?.data?.error?.message || e.message); }
    setLoading(false);
  }, [api, month]);
  useEffect(() => { load(); }, [load]);

  // some phones / in-app browsers block window.prompt (it throws or returns nothing): the decision must still go
  // through (user 2026-10-25: "refuse stays pending"), and each click says whether it worked
  const decide = async (id: string, status: 'approved' | 'refused' | 'pending') => {
    let note: string | null = null;
    if (status === 'refused') { try { note = window.prompt('Why refused? (optional)'); } catch { note = null; } }
    try {
      await api.post(`/buyer-bonus/${id}/decide`, { status, note });
      setD((prev) => (prev ? { ...prev, items: prev.items.map((x) => (x._id === id ? { ...x, status } : x)) } : prev));   // shows at once, then reload
      window.alert(status === 'approved' ? '✅ Approved' : status === 'refused' ? '❌ Refused' : '↩ Back to pending');
    } catch (e: any) { window.alert(`Not saved: ${e?.response?.data?.error?.message || e.message}`); }
    load();
  };
  const saveSettings = async () => { if (!edit) return; await api.post('/buyer-bonus/settings', edit); setEdit(null); load(); };

  // one reward line: the product, what happened, the amount, its status (+ the admin's buttons)
  const row = (x: Reward) => {
    const st = STATUS[x.status];
    const what = x.kind === 'saving'
      ? <>{x.reference} ← <b>{x.price} درهم</b> فالقطعة · وفّرتي {x.savingPiece} × {x.eligibleQty} قطعة = {mad(x.saving || 0)}
          <span className="block text-[11px]" style={{ color: x.referenceSource !== 'cost' ? '#047857' : '#b45309' }}>{x.referenceSource === 'paid' ? `${x.reference} = أرخص ثمن شريناه بيه ف 6 أشهر` : x.referenceSource === 'known' ? `${x.reference} = أرخص ثمن معروف عند المسؤول` : `${x.reference} = التكلفة المتوسطة فالسيستم (ما كاينش ثمن شراء مسجل من قبل)`}</span></>
      : x.kind === 'ontime'
        ? <>جبتي {x.qty} قطعة نهار {dm(x.at)} وباقي فيه {x.stockBefore} قطعة — قبل ما يوصل {d?.settings.stockMin}</>
        : <>قلتي "غير متوفر" نهار {x.reportedAt ? dm(x.reportedAt) : '—'} ولقيتيه نهار {dm(x.at)} ({x.qty} قطعة)</>;
    return (
      <div key={x._id} style={{ borderTop: '1px solid #f1f5f9', marginTop: 8, paddingTop: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {x.image ? <img src={x.image} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, background: '#f8fafc' }} /> : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, direction: 'ltr', textAlign: 'right', fontSize: 13 }}>{x.groupName}</div>
            <div style={{ fontSize: 12.5, color: '#475569' }}>{what}</div>
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 900, color: '#047857' }}>{mad(x.bonus)}</div>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: st.c, background: st.bg, borderRadius: 999, padding: '1px 8px' }}>{st.t}</span>
          </div>
        </div>
        {x.kind === 'saving' && (x.eligibleQty || 0) < x.qty && <div style={{ fontSize: 11.5, color: '#64748b' }}>غير {x.eligibleQty} من {x.qty} قطعة كتحسب (اللي كنبيعو فشهرين)</div>}
        {admin && x.note && <div style={{ fontSize: 11.5, color: '#b91c1c' }}>{x.note}</div>}
        {admin && (
          <div dir="ltr" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {x.suspicious && <span style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 700 }}>⚠️ half price or less — check the receipt</span>}
            {x.kind === 'saving' && <span style={{ fontSize: 11.5, color: x.referenceSource !== 'cost' ? '#64748b' : '#b45309' }}>ref = {x.referenceSource === 'paid' ? 'lowest price really paid (6 months)' : x.referenceSource === 'known' ? 'lowest price known by the admin' : '⚠️ average buying cost — no earlier purchase price recorded, check before approving'}{x.supplier ? ` · ${x.supplier}` : ''}</span>}
            {x.kind !== 'saving' && <span style={{ fontSize: 11.5, color: '#64748b' }}>{x.price ? `${x.price} MAD/pc` : 'no price entered'}{x.supplier ? ` · ${x.supplier}` : ''}</span>}
            {x.status !== 'approved' && <button onClick={() => decide(x._id, 'approved')} style={small('#047857')}>✅ Approve</button>}
            {x.status !== 'refused' && <button onClick={() => decide(x._id, 'refused')} style={small('#b91c1c')}>❌ Refuse</button>}
            {x.status !== 'pending' && <button onClick={() => decide(x._id, 'pending')} style={small('#64748b')}>↩ Pending</button>}
          </div>
        )}
      </div>
    );
  };

  const s = d?.settings;
  // decided rewards leave the list (user 2026-10-25): only what still waits for a decision is shown; she sees the amount
  // accepted per section, the admin can look back with "show decided"
  const all = (k: Kind) => (d?.items || []).filter((x) => x.kind === k);
  // the buyer only ever receives approved rewards (shown in the ✅ line), so she has no list of waiting ones (2026-10-28)
  const of = (k: Kind) => (admin ? all(k).filter((x) => x.status === 'pending' || showDone) : []);
  const doneLine = (k: Kind) => {
    const ok = all(k).filter((x) => x.status === 'approved');
    return ok.length ? <div style={{ marginTop: 6, fontSize: 12.5, color: '#047857', fontWeight: 800 }}>✅ {ok.length} مقبولة = {mad(ok.reduce((t, x) => t + (x.bonus || 0), 0))}</div> : null;
  };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={() => setMonth(shiftMonth(month, -1))} style={nav}>→ الشهر السابق</button>
        <div style={{ fontWeight: 900, fontSize: 16 }}>{monthName(month)}</div>
        <button disabled={month >= thisMonth()} onClick={() => setMonth(shiftMonth(month, 1))} style={{ ...nav, opacity: month >= thisMonth() ? 0.4 : 1 }}>الشهر التالي ←</button>
      </div>
      {err && <div style={{ color: '#b91c1c' }}>⚠️ {err}</div>}
      {!d && !err && <div style={{ color: '#94a3b8' }}>جاري الحساب…</div>}
      {d && s && (<>
        <div style={{ background: 'linear-gradient(135deg,#047857,#059669)', color: '#fff', borderRadius: 18, padding: 16, marginBottom: 12, opacity: loading ? 0.7 : 1 }}>
          <div style={{ fontSize: 14, opacity: 0.9 }}>مكافأتك {d.final ? 'ديال' : 'حتى لليوم —'} {monthName(d.month)}</div>
          <div style={{ fontSize: 34, fontWeight: 900, margin: '2px 0' }}>{mad(d.total)}</div>
          {admin && d.pending > 0 && <div style={{ fontSize: 13.5 }}>+ {mad(d.pending)} فالانتظار (خاص موافقة المسؤول)</div>}
          {d.capped && <div style={{ fontSize: 12.5, opacity: 0.9 }}>وصلتي الحد الأقصى ديال الشهر ({mad(s.monthlyCap)})</div>}
        </div>
        {admin && (d.items || []).some((x) => x.status !== 'pending') && (
          <button onClick={() => setShowDone((v) => !v)} style={{ ...nav, marginBottom: 10 }}>{showDone ? 'Hide decided' : `Show decided (${(d.items || []).filter((x) => x.status !== 'pending').length})`}</button>
        )}

        <Section icon="💰" title="ثمن أحسن" earned={d.parts.savings}>
          كل مرة كتشري بثمن <b>أقل من أرخص ثمن شريناه فـ 6 أشهر</b>، كتاخذي <b>{s.savingPct}%</b> من الفرق.
          {all('saving').length === 0 ? <div style={{ color: '#94a3b8', marginTop: 4 }}>حتى شي توفير هاد الشهر. ملي تضغطي ✅ اشتريت، دخلي الثمن ديال القطعة.</div> : <>{doneLine('saving')}{of('saving').map(row)}</>}
        </Section>

        <Section icon="⏱️" title={`جبتي المنتج قبل ما يوصل ${s.stockMin} قطعة`} earned={d.parts.ontime}>
          كل مرة كتجيبي واحد من {s.stockTop} المنتجات اللي كتبيع بزاف <b>وباقي فيه {s.stockMin} قطعة أو أكثر</b> → <b>{mad(s.ontimeAmount)}</b>.
          {all('ontime').length === 0 ? <div style={{ color: '#94a3b8', marginTop: 4 }}>حتى شي وحدة هاد الشهر.</div> : <>{doneLine('ontime')}{of('ontime').map(row)}</>}
          <div style={{ marginTop: 8, fontWeight: 800 }}>المنتجات اللي خاصها ديما {s.stockMin}+ :</div>
          {d.bestSellers.map((x) => {
            const bad = x.stock <= 0, low = !bad && x.stock < s.stockMin;
            return (
              <div key={x.groupId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ flex: 1, minWidth: 0, direction: 'ltr', textAlign: 'right', fontSize: 12.5, fontWeight: 700 }}>{x.name}</span>
                <span style={{ fontSize: 12.5, fontWeight: 900, borderRadius: 999, padding: '1px 8px', whiteSpace: 'nowrap', color: bad ? '#b91c1c' : low ? '#b45309' : '#047857', background: bad ? '#fef2f2' : low ? '#fffbeb' : '#ecfdf5' }}>{x.stock} قطعة</span>
              </div>);
          })}
        </Section>

        <Section icon="🔎" title="لقيتي منتج كان ناقص" earned={d.parts.found}>
          منتج كيبيع مزيان وقلتي عليه <b>"❌ غير متوفر"</b> — ملي تلقايه فين ما كان وتجيبيه → <b>{mad(s.foundAmount)}</b>.
          {all('found').length === 0 ? <div style={{ color: '#94a3b8', marginTop: 4 }}>حتى شي وحدة هاد الشهر.</div> : <>{doneLine('found')}{of('found').map(row)}</>}
        </Section>

        <button onClick={() => setShowRules((v) => !v)} style={{ width: '100%', minHeight: 44, borderRadius: 12, border: '1px solid #cbd5e1', background: '#f8fafc', fontWeight: 800, marginBottom: 10, cursor: 'pointer' }}>{showRules ? '▲' : '▼'} كيفاش كتحسب المكافأة؟</button>
        {showRules && (
          <div style={{ ...card, fontSize: 14, lineHeight: 1.8 }}>
            <b>الهدف: المنتجات اللي خدامة لينا عمرها ما توقف.</b>
            <ol style={{ paddingInlineStart: 20, margin: '6px 0' }}>
              <li><b>ثمن أحسن 💰</b> — إلا شريتي بثمن أقل من أرخص ثمن خلصناه فـ 6 أشهر، كتاخذي {s.savingPct}% من الفرق (غير القطع اللي كنبيعو فشهرين). مرة وحدة على كل ثمن جديد.</li>
              <li><b>فالوقت ⏱️</b> — كتجيبي منتج من اللي كيبيعو بزاف قبل ما ينزل تحت {s.stockMin} قطعة → {mad(s.ontimeAmount)}. ماشي بكري بزاف (ماشي باش نعمرو المخزن)، ومرة وحدة فـ 14 يوم لكل منتج. لائحة الشراء كتوريك امتى خاصك تجيبيه (اليوم).</li>
              <li><b>لقيتيه 🔎</b> — منتج كيبيع مزيان، قلتي "غير متوفر"، ومن بعد لقيتيه وجبتيه → {mad(s.foundAmount)}.</li>
            </ol>
            خاص الشراء يكون 10 قطع أو أكثر وبثمن معقول. كل مكافأة خاصها موافقة المسؤول، وكتبان هنا ملي تتقبل — احتافظي بالفاتورة. الحد الأقصى فالشهر: <b>{mad(s.monthlyCap)}</b>.
          </div>
        )}

        {admin && (
          <div dir="ltr" style={{ ...card, background: '#f5f3ff', border: '1px solid #ddd6fe' }}>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>⚙️ Bonus settings (admin)</div>
            {!edit ? (
              <div style={{ fontSize: 12.5, color: '#475569' }}>
                Saving share {s.savingPct}% · on time {s.ontimeAmount} MAD · found {s.foundAmount} MAD · top {s.stockTop} best sellers, never under {s.stockMin} pcs · monthly cap {s.monthlyCap} MAD
                <button onClick={() => setEdit({ ...s })} style={{ ...small('#7c3aed'), marginLeft: 8 }}>Edit</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                {([['savingPct', 'Saving share %'], ['ontimeAmount', 'On-time MAD'], ['foundAmount', 'Found MAD'], ['stockTop', 'Best sellers watched'], ['stockMin', 'Never under (pcs)'], ['monthlyCap', 'Monthly cap MAD']] as [keyof Settings, string][]).map(([k, label]) => (
                  <label key={k} style={{ fontSize: 11.5 }}>{label}<br /><input type="number" value={edit[k]} onChange={(e) => setEdit({ ...edit, [k]: Number(e.target.value) })} style={{ width: 110, minHeight: 32, borderRadius: 8, border: '1px solid #c4b5fd', padding: '0 6px' }} /></label>
                ))}
                <button onClick={saveSettings} style={small('#7c3aed')}>Save</button>
                <button onClick={() => setEdit(null)} style={small('#94a3b8')}>Cancel</button>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
};

const nav: React.CSSProperties = { minHeight: 36, borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', padding: '0 10px', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const small = (bg: string): React.CSSProperties => ({ minHeight: 30, borderRadius: 8, border: 'none', background: bg, color: '#fff', fontWeight: 700, padding: '0 10px', fontSize: 12, cursor: 'pointer' });

export default BuyerBonus;
